import { useSession } from "@src/appV2/Auth/api/useSession";
import { useCredentialSharing } from "@src/appV2/Professionals/hooks/useCredentialSharing";
import { ShareCredentialsDialog } from "@src/appV2/redesign/MyProfessionals/components/ShareCredentialsDialog";
import { useModalState } from "@src/lib/ui-react";
import { type ReactNode } from "react";

interface UseShareWorkerCredentialsParams {
  workerId: string;
  workplaceId: string;
  workerName?: string;
  /**
   * When `true`, the calling surface explicitly doesn't support credential
   * sharing (e.g. placement channels in chat). Callers map their domain
   * concept to this boolean. Mirrors `useInviteWorkerToShift`.
   */
  surfaceDisabled?: boolean;
}

interface UseShareWorkerCredentialsResult {
  canShareCredentials: boolean;
  openShareCredentialsDialog: () => void;
  shareCredentialsDialogElement: ReactNode;
}

/**
 * Single source of truth for sharing a specific worker's credentials from
 * chat. Consumers wire `openShareCredentialsDialog` to a click target when
 * `canShareCredentials` is true and render `shareCredentialsDialogElement`
 * somewhere in the tree.
 *
 * Renders the redesign `ShareCredentialsDialog` (`source="workplace-chat"`),
 * replacing the legacy `ShareCredentialsModal` that chat previously used.
 *
 * Gating mirrors the prior inline header affordance: credential sharing must
 * be enabled for the workplace, and the affordance is hidden for admins,
 * worker sessions, and surfaces that opt out via `surfaceDisabled` (e.g.
 * placement channels, which never showed credential sharing).
 */
export function useShareWorkerCredentials(
  params: UseShareWorkerCredentialsParams
): UseShareWorkerCredentialsResult {
  const { workerId, workplaceId, workerName, surfaceDisabled } = params;

  const session = useSession();
  const isAdmin = Boolean(session.admin);
  const isWorkerSession = session.type === "AGENT";

  const { isEnabled } = useCredentialSharing();

  const modalState = useModalState();

  const canShareCredentials = isEnabled && !isAdmin && !isWorkerSession && !surfaceDisabled;

  const shareCredentialsDialogElement = modalState.modalIsOpen ? (
    <ShareCredentialsDialog
      modalState={modalState}
      source="workplace-chat"
      workerId={workerId}
      workplaceId={workplaceId}
      workerName={workerName}
      onSuccess={modalState.closeModal}
    />
  ) : null;

  return {
    canShareCredentials,
    openShareCredentialsDialog: modalState.openModal,
    shareCredentialsDialogElement,
  };
}
